import type { LaborRoleMappingEntry, TipRoleGroupEntry } from '@/types/payrollPaymaster'

/** Paymaster settings helpers (WS7). */

export const SHARE_SUM_TOLERANCE = 1e-9

export const PAYMASTER_CLOVER_MERCHANTS = [
  { id: 'VF0RA23FN28W1', label: 'River Oaks' },
  { id: 'DM9TTXKWXDKW1', label: 'Highland Park' },
] as const

export const LABOR_GROUP_OPTIONS = ['BOH', 'FOH', 'GA', 'Support'] as const

export const TIP_GROUP_LABOR_GROUP = 'FOH'

export function canAssignTipGroup(laborGroup: string): boolean {
  return laborGroup === TIP_GROUP_LABOR_GROUP
}

export function maskSecret(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.length <= 4) return '····'
  return `····${trimmed.slice(-4)}`
}

export function parseShareInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const normalized = trimmed.endsWith('%') ? trimmed.slice(0, -1) : trimmed
  const value = Number(normalized)
  if (!Number.isFinite(value)) return null
  return trimmed.endsWith('%') ? value / 100 : value
}

export function formatShareInput(share: number, asPercent: boolean): string {
  if (asPercent) {
    return `${(share * 100).toFixed(2).replace(/\.?0+$/, '')}%`
  }
  return String(share)
}

export function validateNonzeroShareSum(shares: Record<string, number>): string | null {
  const total = Object.values(shares).reduce((sum, share) => {
    if (share === 0) return sum
    return sum + share
  }, 0)
  if (Math.abs(total - 1) > SHARE_SUM_TOLERANCE) {
    return `Non-zero shares must sum to 100% (got ${(total * 100).toFixed(2)}%)`
  }
  return null
}

export type MergedRoleRow = {
  role: string
  tipGroup: string
  laborGroup: string
  station: string
  locationType: string
  eligible: boolean
  excludeFromDenominators: boolean
}

export function incompleteRolesWarningMessage(roles: string[]): string {
  return `Review Payroll Settings → Roles and assign a labor group, location, and station (when not excluded) for: ${roles.join(', ')}.`
}

export function incompleteLaborRoleNames(rows: MergedRoleRow[]): string[] {
  return rows
    .filter(
      (row) =>
        !row.laborGroup.trim() ||
        !row.locationType.trim() ||
        (!row.excludeFromDenominators && !row.station.trim()),
    )
    .map((row) => row.role)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

export function tipGroupNamesFromShareRows(rows: Array<{ group: string }>): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const row of rows) {
    const group = row.group.trim()
    if (!group || seen.has(group)) continue
    seen.add(group)
    names.push(group)
  }
  return names
}

export function renameTipGroupOnRoleRows(
  rows: MergedRoleRow[],
  from: string,
  to: string
): MergedRoleRow[] {
  const source = from.trim()
  if (!source) return rows
  const next = to.trim()
  return rows.map((row) => (row.tipGroup.trim() === source ? { ...row, tipGroup: next } : row))
}

export function isOrphanTipGroup(tipGroup: string, validNames: readonly string[]): boolean {
  const value = tipGroup.trim()
  if (!value) return false
  return !validNames.includes(value)
}

export function stationNamesFromRows(rows: Array<{ station: string }>): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const row of rows) {
    const station = row.station.trim()
    if (!station || seen.has(station)) continue
    seen.add(station)
    names.push(station)
  }
  return names
}

export function stationNamesFromRoleMapping(mapping: unknown): string[] {
  if (mapping == null || typeof mapping !== 'object' || Array.isArray(mapping)) return []
  const seen = new Set<string>()
  const names: string[] = []
  for (const entry of Object.values(mapping as Record<string, unknown>)) {
    if (entry == null || typeof entry !== 'object') continue
    const station = String((entry as { station?: unknown }).station ?? '').trim()
    if (!station || seen.has(station)) continue
    seen.add(station)
    names.push(station)
  }
  return names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

export function validateStationNames(rows: Array<{ station: string }>): string | null {
  const seen = new Set<string>()
  for (const row of rows) {
    const station = row.station.trim()
    if (!station) {
      return 'Station names cannot be blank'
    }
    if (seen.has(station)) {
      return `Duplicate station name: ${station}`
    }
    seen.add(station)
  }
  return null
}

export function renameStationOnRoleRows(
  rows: MergedRoleRow[],
  from: string,
  to: string,
): MergedRoleRow[] {
  const source = from.trim()
  if (!source) return rows
  const next = to.trim()
  return rows.map((row) => (row.station.trim() === source ? { ...row, station: next } : row))
}

export function clearStationOnRoleRows(
  rows: MergedRoleRow[],
  removedStation: string,
): MergedRoleRow[] {
  const source = removedStation.trim()
  if (!source) return rows
  return rows.map((row) =>
    row.station.trim() === source ? { ...row, station: '' } : row,
  )
}

export type StationRow = {
  station: string
  locked: boolean
}

export type LaborAnalyticsStationsSetting = {
  names?: string[]
  locked?: string[]
}

export function mergeStationRows(setting: LaborAnalyticsStationsSetting): StationRow[] {
  const names: string[] = []
  const seen = new Set<string>()
  for (const raw of setting.names ?? []) {
    const station = raw.trim()
    if (!station || seen.has(station)) continue
    seen.add(station)
    names.push(station)
  }
  const lockedSet = new Set<string>()
  for (const raw of setting.locked ?? []) {
    const station = raw.trim()
    if (!station) continue
    lockedSet.add(station)
    if (!seen.has(station)) {
      names.push(station)
      seen.add(station)
    }
  }
  return names.map((station) => ({ station, locked: lockedSet.has(station) }))
}

export function splitStationRows(rows: StationRow[]): LaborAnalyticsStationsSetting {
  const names: string[] = []
  const locked: string[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    const station = row.station.trim()
    if (!station || seen.has(station)) continue
    seen.add(station)
    names.push(station)
    if (row.locked) locked.push(station)
  }
  return { names, locked }
}

export function isOrphanStation(station: string, validNames: readonly string[]): boolean {
  const value = station.trim()
  if (!value) return false
  return !validNames.includes(value)
}

export function mergeRoleRows(
  tipRoles: Record<string, TipRoleGroupEntry>,
  laborRoles: Record<string, LaborRoleMappingEntry>,
  includedStations: string[]
): MergedRoleRow[] {
  const included = new Set(includedStations.map((role) => role.trim()).filter(Boolean))
  const roles = new Set([
    ...Object.keys(tipRoles),
    ...Object.keys(laborRoles),
    ...includedStations,
  ])
  return [...roles]
    .map((role) => role.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((role) => {
      const tip = tipRoles[role]
      const labor = laborRoles[role]
      return {
        role,
        tipGroup: tip?.group ?? '',
        laborGroup: labor?.group ?? '',
        station: labor?.station ?? '',
        locationType: labor?.location_type ?? '',
        eligible: tip?.eligible ?? false,
        excludeFromDenominators: !included.has(role),
      }
    })
}

export function splitMergedRoleRows(rows: MergedRoleRow[]): {
  tipRoles: Record<string, TipRoleGroupEntry>
  laborRoles: Record<string, LaborRoleMappingEntry>
  includedStations: string[]
} {
  const tipRoles: Record<string, TipRoleGroupEntry> = {}
  const laborRoles: Record<string, LaborRoleMappingEntry> = {}
  const includedStations: string[] = []
  for (const row of rows) {
    const role = row.role.trim()
    if (!role) continue
    const tipGroup = canAssignTipGroup(row.laborGroup) ? row.tipGroup.trim() : ''
    if (tipGroup) {
      tipRoles[role] = { group: tipGroup, eligible: row.eligible }
    }
    const laborGroup = row.laborGroup.trim()
    const locationType = row.locationType.trim()
    const station = row.station.trim()
    laborRoles[role] = {
      group: laborGroup,
      location_type: locationType,
      station,
    }
    if (!row.excludeFromDenominators) {
      includedStations.push(role)
    }
  }
  return { tipRoles, laborRoles, includedStations }
}
